import React from 'react'
import { useTranslation } from 'react-i18next'
import ChatStatsRing from './ChatStatsRing'
import { CARD, SectionLabel } from './ChatStatsSection'
import { formatDelta, formatDuration, formatNumber, percentChange } from './chatStatsFormat'

const ringTone = (percent) => {
    if (percent >= 80) return { color: '#186c2d', track: 'rgba(111, 204, 135, 0.3)', tint: 'rgba(111, 204, 135, 0.14)' }
    if (percent >= 50) return { color: '#9c6f11', track: 'rgba(242, 201, 76, 0.35)', tint: 'rgba(242, 201, 76, 0.16)' }
    return { color: '#b21f2b', track: 'rgba(252, 76, 89, 0.22)', tint: 'rgba(252, 76, 89, 0.09)' }
}

function Tile({ label, value, delta, sub, tint }) {
    return (
        <div style={{ flex: 1, minWidth: 200, background: tint, borderRadius: 14, padding: '16px 18px' }}>
            <div style={{ fontSize: 13, color: '#8e8fb4', fontWeight: 700 }}>{label}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
                <span style={{ fontSize: 26, fontWeight: 800, color: '#2d2b45', lineHeight: 1 }}>{value}</span>
                {delta && <span style={{ fontSize: 13, fontWeight: 700, color: delta.color }}>{delta.text}</span>}
            </div>
            <div style={{ fontSize: 12, color: '#8e8fb4', marginTop: 7 }}>{sub}</div>
        </div>
    )
}

function ChatStatsResponsiveness({ stats }) {
    const { t } = useTranslation()
    const responsiveness = stats.responsiveness
    const tone = ringTone(responsiveness.within_24h_percent)

    const firstDelta = formatDelta(percentChange(responsiveness.first_response_ms, responsiveness.first_response_previous_ms), { lowerIsBetter: true })
    const sosDelta = formatDelta(percentChange(responsiveness.sos_response_ms, responsiveness.sos_response_previous_ms), { lowerIsBetter: true })

    return (
        <>
            <SectionLabel number={2}>{t('chatStatsSection2')}</SectionLabel>

            <div style={{ ...CARD, padding: '22px 24px', marginBottom: 26 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: '#2d2b45' }}>{t('chatStatsResponsiveness')}</span>
                    <span style={{ background: '#e4e3f0', color: '#5e639b', fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 999 }}>
                        {t('chatStatsDaysBadge', { count: stats.period.days })}
                    </span>
                </div>

                <div style={{ display: 'flex', gap: 20, alignItems: 'stretch', flexWrap: 'wrap' }}>
                    <div style={{ flex: 'none', width: 250, display: 'flex', alignItems: 'center', gap: 18, background: tone.tint, borderRadius: 14, padding: '18px 20px' }}>
                        <ChatStatsRing percent={responsiveness.within_24h_percent} color={tone.color} track={tone.track} />
                        <div style={{ fontSize: 13, color: '#2d2b45', lineHeight: 1.4 }}>{t('chatStatsWithin24h')}</div>
                    </div>

                    <div style={{ flex: 1, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <Tile
                            label={t('chatStatsAvgFirstResponse')}
                            value={formatDuration(responsiveness.first_response_ms)}
                            delta={firstDelta}
                            sub={
                                responsiveness.first_response_previous_ms
                                    ? t('chatStatsWasLastPeriod', { value: formatDuration(responsiveness.first_response_previous_ms) })
                                    : t('chatStatsNoPreviousPeriod')
                            }
                            tint="rgba(94, 99, 155, 0.08)"
                        />
                        <Tile
                            label={t('chatStatsAvgSosResponse')}
                            value={formatDuration(responsiveness.sos_response_ms)}
                            delta={sosDelta}
                            sub={t('chatStatsPriorityTier')}
                            tint="rgba(244, 124, 57, 0.14)"
                        />
                    </div>
                </div>

                {responsiveness.answered_late > 0 && (
                    <div style={{ fontSize: 12, color: '#8e8fb4', marginTop: 14 }}>{t('chatStatsAnsweredLate', { count: formatNumber(responsiveness.answered_late) })}</div>
                )}
            </div>
        </>
    )
}

export default ChatStatsResponsiveness
